import {
  Box,
  Heading,
  Text,
  Wrap,
  WrapItem,
  Stack,
  Grid,
  Flex,
} from "@chakra-ui/react";
import NextImage from "next/image";
import { FC } from "react";

import Button from "components/Chakra/Button";
import CustomLink from "components/Chakra/CustomLink";
import useRouter from "hooks/useRouter";
import { useAlertModal } from "modals/useAlertModal";
import MetaHead from "components/MetaHead";
import BulletList from "scenes/Pricelist/components/BulletList";
import { EOrderTypeId } from "utils/payment";
import countRenters from "config/countRenters";
import { ReactFCC } from "src/types/types";
import IconCancel from "components/Chakra/Icons/Cancel";
import IconInfo from "components/Chakra/Icons/Info";
import IconSuccessCircle from "components/Chakra/Icons/SuccessCircle";

const Card: ReactFCC<{
  height?: "normal" | "tall";
}> = ({ children, height = "normal" }) => (
  <Box
    borderRadius="1rem"
    position="relative"
    boxShadow="rgba(0, 0, 0, 0.24) 0rem 0.188rem 1rem"
    h={
      height === "normal"
        ? ["25.625rem", "30rem", "30rem", "25.625rem"]
        : "30rem"
    }
    py="1.25rem"
  >
    {children}
  </Box>
);

const Pricelist: FC = () => {
  const {
    query: { offerId },
  } = useRouter();

  const { openModal: openAlertModal } = useAlertModal();

  return (
    <>
      <MetaHead
        title="Ceník inzerce – inzerujte už od 49 Kč"
        description="Inzerujte na největším českém portálu na pronájmy a zaujměte ty nejkvalitnější zájemce. To vše už od 49 Kč."
      />
      <Box mb="2rem" mt="3.125rem">
        <Box>
          {offerId ? (
            <Stack alignItems="center" px="1rem">
              <Heading
                textAlign="center"
                as="h1"
                data-test="methodOfPublication.heading"
              >
                Vyberte způsob zveřejnění:
              </Heading>
            </Stack>
          ) : (
            <Stack alignItems="center">
              <Heading as="h1">Výhodná inzerce</Heading>
              <Text pt="0.5rem" variant="large_2" textAlign="center">
                Inzerovalo u nás již
                <Box as="span" fontWeight="bold">{` ${countRenters()} `}</Box>
                spokojených pronajímatelů a makléřů.
              </Text>
            </Stack>
          )}
        </Box>
      </Box>

      <Wrap
        spacing="1.25rem"
        direction="row"
        mb="6.25rem"
        justify="center"
        mx="5%"
      >
        <WrapItem
          alignItems="center"
          pt={["0.9rem", "0.9rem", "unset"]}
          data-test="methodOfPublication.start"
        >
          <Card height="normal">
            <Flex
              h="100%"
              direction="column"
              justify="space-between"
              align="center"
            >
              <Box>
                <Stack align="center" mb="1rem">
                  <Heading as="h2" variant="h3">
                    Start
                  </Heading>
                  <Heading as="h2" variant="h3" textColor="ud-primary">
                    49 Kč
                  </Heading>
                </Stack>
                <BulletList
                  lines={[
                    {
                      color: "gray",
                      content: (
                        <>
                          <Text variant="small_2" mb="1rem">
                            Odložená možnost kontaktování inzerátu
                          </Text>
                          <Text variant="small_2" color="ud-grey-400">
                            <i>
                              První 3 dny vás mohou oslovit pouze zájemci s
                              Premium účtem.
                            </i>
                          </Text>
                        </>
                      ),
                    },
                    {
                      color: "gray",
                      content: (
                        <>
                          <Text variant="small_2" mb="1rem">
                            Inzerát bude zveřejněn 30 dní
                          </Text>
                          <Text variant="small_2" color="ud-grey-400">
                            <i>
                              Pro neomezenou platnost zvolte balíček VIP
                              inzerce.
                            </i>
                          </Text>
                        </>
                      ),
                    },
                  ]}
                />
              </Box>

              <CustomLink
                dataTest="methodOfPublication.start.button"
                trackEventPayload={{
                  "dl.eCat": "offer_create",
                  "dl.eAct": "offer.clicked",
                  "dl.eLab": "Vložit Start",
                }}
                to={offerId ? "/platba/vlozeni-inzeratu" : "/vlozeni-inzeratu"}
                queryProps={
                  offerId
                    ? {
                        tid: EOrderTypeId.INSERT_OFFER_FREE.toString(),
                        oid: offerId?.toString() as string,
                      }
                    : { tid: EOrderTypeId.INSERT_OFFER_FREE.toString() }
                }
                w="10rem"
                variant="button_outline"
              >
                Vložit Start
              </CustomLink>
            </Flex>
          </Card>
        </WrapItem>

        <WrapItem
          alignItems="center"
          py="0.9rem"
          data-test="methodOfPublication.vip"
        >
          <Card height="tall">
            <Flex
              h="100%"
              direction="column"
              justify="space-between"
              align="center"
            >
              <Box>
                <Stack
                  direction="column"
                  justify="center"
                  alignItems="center"
                  mb="1rem"
                >
                  <Box boxSize="4rem" position="relative">
                    <NextImage
                      priority
                      src="/img/icons/raketa.svg"
                      alt=""
                      fill
                      style={{
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                  <Heading as="h2" variant="h3">
                    VIP
                  </Heading>
                  <Heading as="h2" variant="h3" textColor="ud-primary">
                    399 Kč
                  </Heading>
                </Stack>
                <BulletList
                  lines={[
                    {
                      color: "green",
                      content: (
                        <Text variant="small_2" textAlign="left">
                          Možnost kontaktovat ihned
                        </Text>
                      ),
                    },
                    {
                      color: "green",
                      content: (
                        <Text variant="small_2" textAlign="left">
                          Neomezená platnost inzerce
                        </Text>
                      ),
                    },
                    {
                      color: "green",
                      content: (
                        <Text variant="small_2" textAlign="left">
                          Výpis inzerátu mezi prvními
                        </Text>
                      ),
                    },
                    {
                      color: "green",
                      content: (
                        <Text variant="small_2" textAlign="left">
                          Zobrazení v doporučených
                        </Text>
                      ),
                    },
                  ]}
                />
              </Box>

              <CustomLink
                dataTest="methodOfPublication.vip.button"
                variant="button"
                to={offerId ? "/platba/vlozeni-inzeratu" : "/vlozeni-inzeratu"}
                queryProps={
                  offerId
                    ? {
                        tid: EOrderTypeId.INSERT_OFFER_VIP.toString(),
                        oid: offerId?.toString() as string,
                      }
                    : { tid: EOrderTypeId.INSERT_OFFER_VIP.toString() }
                }
                trackEventPayload={{
                  "dl.eCat": "offer_create",
                  "dl.eAct": "offer.clicked",
                  "dl.eLab": "Vložit VIP",
                }}
                w="10rem"
              >
                Vložit VIP
              </CustomLink>
            </Flex>
          </Card>
        </WrapItem>

        <WrapItem
          alignItems="center"
          pb={["0.9rem", "0.9rem", "unset"]}
          data-test="methodOfPublication.standard"
        >
          <Card height="normal">
            <Flex
              h="100%"
              direction="column"
              justify="space-between"
              align="center"
            >
              <Box>
                <Stack alignItems="center" mb="1rem">
                  <Heading as="h2" variant="h3">
                    Standard
                  </Heading>
                  <Heading as="h2" variant="h3" textColor="ud-primary">
                    149 Kč
                  </Heading>
                </Stack>
                <Stack>
                  <BulletList
                    lines={[
                      {
                        color: "green",
                        content: (
                          <Text variant="small_2" textAlign="left">
                            Možnost kontaktovat ihned
                          </Text>
                        ),
                      },
                      {
                        color: "gray",
                        content: (
                          <>
                            <Text variant="small_2" mb="1rem">
                              Inzerát bude zveřejněn 30 dní
                            </Text>
                            <Text variant="small_2" color="ud-grey-400">
                              <i>
                                Pro neomezenou platnost zvolte balíček VIP
                                inzerce.
                              </i>
                            </Text>
                          </>
                        ),
                      },
                    ]}
                  />
                </Stack>
              </Box>

              <CustomLink
                dataTest="methodOfPublication.standard.button"
                variant="button_outline"
                to={offerId ? "/platba/vlozeni-inzeratu" : "/vlozeni-inzeratu"}
                queryProps={
                  offerId
                    ? {
                        tid: EOrderTypeId.INSERT_OFFER_STANDARD.toString(),
                        oid: offerId?.toString() as string,
                      }
                    : { tid: EOrderTypeId.INSERT_OFFER_STANDARD.toString() }
                }
                trackEventPayload={{
                  "dl.eCat": "offer_create",
                  "dl.eAct": "offer.clicked",
                  "dl.eLab": "Vložit Standard",
                }}
                w="10rem"
              >
                Vložit Standard
              </CustomLink>
            </Flex>
          </Card>
        </WrapItem>
      </Wrap>

      <Box
        display={{ base: "none", md: "block" }}
        maxWidth="56.25rem"
        px="2rem"
        mx="auto"
        mb="3rem"
      >
        <Stack mb="3rem" alignItems="center">
          <Heading as="h2" fontWeight="light">
            Přehled inzerce
          </Heading>
        </Stack>

        <Grid mb="1rem" gridTemplateColumns="46% 18% 18% 18%">
          <Stack pl="1.25rem" alignItems="flex-start">
            <Text textColor="ud-primary" textAlign="left" fontWeight="bold">
              Parametry
            </Text>
          </Stack>
          <Stack alignItems="center">
            <Text textColor="ud-primary" fontWeight="bold">
              Start
            </Text>
          </Stack>
          <Stack alignItems="center">
            <Text textColor="ud-primary" fontWeight="bold">
              Standard
            </Text>
          </Stack>
          <Stack alignItems="center">
            <Text textColor="ud-primary" fontWeight="bold">
              VIP
            </Text>
          </Stack>
        </Grid>

        <Grid
          py="0.625rem"
          borderTop="1px solid"
          borderColor="ud-grey-300"
          gridTemplateColumns="46% 18% 18% 18%"
        >
          <Stack pl="1.25rem" alignItems="center" direction="row">
            <Text textAlign="left">Podrobné informace o zájemcích</Text>
            <Button
              variant="link"
              onClick={() =>
                openAlertModal({
                  children: (
                    <Stack spacing={0}>
                      <Stack
                        justify="center"
                        alignItems="center"
                        spacing={0}
                        position="relative"
                        w="20rem"
                        h="25rem"
                      >
                        <NextImage
                          src="/img/common/vizitka.jpg"
                          alt="Náhled vizitky"
                          fill
                        />
                      </Stack>
                      <Text textAlign="center" mt="1rem">
                        O zájemcích víme více. Dozvíte se, jestli pracují, či
                        studují, kolik jim je let, kdy se chtějí stěhovat,
                        jestli mají zvíře a jiné užitečné informace.
                      </Text>
                    </Stack>
                  ),
                })
              }
            >
              <IconInfo fillWith="ud-grey-300" hoverColor="ud-grey-400" />
            </Button>
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
        </Grid>
        <Grid
          py="0.625rem"
          borderTop="1px solid"
          borderColor="ud-grey-300"
          gridTemplateColumns="46% 18% 18% 18%"
        >
          <Stack pl="1.25rem" alignItems="center" direction="row">
            <Text textAlign="left">Doplnění zajímavostí k inzerátu</Text>
            <Button
              variant="link"
              onClick={() =>
                openAlertModal({
                  children:
                    "Ke každému inzerátu ve velkém městě přidáváme zajímavosti o dané čtvrti, o době cesty do centra a jiné. Tím se stane váš inzerát atraktivnějším a dokáže nájemníkovi lépe přiblížit bydlení v dané lokalitě.",
                })
              }
            >
              <IconInfo fillWith="ud-grey-300" hoverColor="ud-grey-400" />
            </Button>
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
        </Grid>
        <Grid
          py="0.625rem"
          borderTop="1px solid"
          borderColor="ud-grey-300"
          gridTemplateColumns="46% 18% 18% 18%"
        >
          <Stack pl="1.25rem" alignItems="center" direction="row">
            <Text textAlign="left">Přehledná komunikace se zájemci</Text>
            <CustomLink
              onClick={() =>
                openAlertModal({
                  children:
                    "U každého inzerátu máte pohromadě všechny zprávy od zájemců. Vyřídíte tak veškerou komunikaci snadně a přehledně.",
                })
              }
            >
              <IconInfo fillWith="ud-grey-300" hoverColor="ud-grey-400" />
            </CustomLink>
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
        </Grid>
        <Grid
          py="0.625rem"
          borderTop="1px solid"
          borderColor="ud-grey-300"
          gridTemplateColumns="46% 18% 18% 18%"
        >
          <Stack pl="1.25rem" align="center" direction="row">
            <Text textAlign="left">Délka uveřejnění nabídky</Text>
          </Stack>
          <Stack alignItems="center">
            <Text textAlign="center" fontWeight="bold">
              30 dnů
            </Text>
          </Stack>
          <Stack alignItems="center">
            <Text textAlign="center" fontWeight="bold">
              30 dnů
            </Text>
          </Stack>
          <Stack alignItems="center">
            <Text textAlign="center" fontWeight="bold">
              neomezeně
            </Text>
          </Stack>
        </Grid>
        <Grid
          py="0.625rem"
          borderTop="1px solid"
          borderColor="ud-grey-300"
          gridTemplateColumns="46% 18% 18% 18%"
        >
          <Stack pl="1.25rem" align="center" direction="row">
            <Text textAlign="left">Kontaktování inzerátu ihned</Text>
            <Button
              variant="link"
              onClick={() =>
                openAlertModal({
                  children:
                    "U balíčků VIP a Standard vás mohou ihned kontaktovat všichni zájemci. S inzercí Start vás mají možnost zkontaktovat pouze zájemci se zakoupeným Premium účtem, ostatní váš kontakt u inzerátu uvidí až po 72 hodinách od zveřejnění.",
                })
              }
            >
              <IconInfo fillWith="ud-grey-300" hoverColor="ud-grey-400" />
            </Button>
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconCancel fillWith="ud-grey-300" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
        </Grid>
        <Grid
          py="0.625rem"
          borderTop="1px solid"
          borderColor="ud-grey-300"
          gridTemplateColumns="46% 18% 18% 18%"
        >
          <Stack pl="1.25rem" align="center" direction="row">
            <Text textAlign="left">Přednostní výpis ve vyhledávání</Text>
            <CustomLink
              onClick={() =>
                openAlertModal({
                  children:
                    "Váš inzerát bude více vidět, zvýhodníme jej při výpisu jednotlivých nabídek oproti konkurenčním. Výrazně se tím zvýší počet zhlédnutí zájemci.",
                })
              }
            >
              <IconInfo fillWith="ud-grey-300" hoverColor="ud-grey-400" />
            </CustomLink>
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconCancel fillWith="ud-grey-300" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconCancel fillWith="ud-grey-300" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
        </Grid>
        <Grid
          py="0.625rem"
          borderTop="1px solid"
          borderColor="ud-grey-300"
          gridTemplateColumns="46% 18% 18% 18%"
        >
          <Stack pl="1.25rem" align="center" direction="row">
            <Text textAlign="left">
              Doporučení inzerátu u podobných nabídek
            </Text>
            <Button
              variant="link"
              onClick={() =>
                openAlertModal({
                  children:
                    "Váš inzerát doporučíme v detailu podobných nabídek. Díky tomu inzerát uvidí mnohem více zájemců.",
                })
              }
            >
              <IconInfo fillWith="ud-grey-300" hoverColor="ud-grey-400" />
            </Button>
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconCancel fillWith="ud-grey-300" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconCancel fillWith="ud-grey-300" />
          </Stack>
          <Stack alignItems="center" justify="center">
            <IconSuccessCircle fillWith="ud-success" />
          </Stack>
        </Grid>
        <Grid
          py="0.625rem"
          borderTop="1px solid"
          borderColor="ud-grey-300"
          gridTemplateColumns="46% 18% 18% 18%"
        >
          <Stack pl="1.25rem" align="center" direction="row">
            <Text textAlign="left">Cena</Text>
          </Stack>
          <Stack alignItems="center">
            <Text textAlign="center" fontWeight="bold">
              49 Kč
            </Text>
          </Stack>
          <Stack alignItems="center">
            <Text textAlign="center" fontWeight="bold">
              149 Kč
            </Text>
          </Stack>
          <Stack alignItems="center">
            <Text textAlign="center" fontWeight="bold">
              399 Kč
            </Text>
          </Stack>
        </Grid>
        <Grid py="0.625rem" gridTemplateColumns="46% 18% 18% 18%">
          <Box />
          <Stack justify="center" spacing={0} direction="row">
            <CustomLink
              to={offerId ? "/platba/vlozeni-inzeratu" : "/vlozeni-inzeratu"}
              queryProps={
                offerId
                  ? {
                      tid: EOrderTypeId.INSERT_OFFER_FREE.toString(),
                      oid: offerId.toString() as string,
                    }
                  : { tid: EOrderTypeId.INSERT_OFFER_FREE.toString() }
              }
              trackEventPayload={{
                "dl.eCat": "offer_create",
                "dl.eAct": "offer.clicked",
                "dl.eLab": "Vložit Start",
              }}
              variant="button"
              size="sm"
            >
              Vložit Start
            </CustomLink>
          </Stack>

          <Stack justify="center" spacing={0} direction="row">
            <CustomLink
              variant="button"
              to={offerId ? "/platba/vlozeni-inzeratu" : "/vlozeni-inzeratu"}
              queryProps={
                offerId
                  ? {
                      tid: EOrderTypeId.INSERT_OFFER_STANDARD.toString(),
                      oid: offerId?.toString() as string,
                    }
                  : { tid: EOrderTypeId.INSERT_OFFER_STANDARD.toString() }
              }
              trackEventPayload={{
                "dl.eCat": "offer_create",
                "dl.eAct": "offer.clicked",
                "dl.eLab": "Vložit Standard",
              }}
              size="sm"
            >
              Vložit Standard
            </CustomLink>
          </Stack>

          <Stack justify="center" spacing={0} direction="row">
            <CustomLink
              variant="button"
              to={offerId ? "/platba/vlozeni-inzeratu" : "/vlozeni-inzeratu"}
              queryProps={
                offerId
                  ? {
                      tid: EOrderTypeId.INSERT_OFFER_VIP.toString(),
                      oid: offerId?.toString() as string,
                    }
                  : { tid: EOrderTypeId.INSERT_OFFER_VIP.toString() }
              }
              trackEventPayload={{
                "dl.eCat": "offer_create",
                "dl.eAct": "offer.clicked",
                "dl.eLab": "Vložit VIP",
              }}
              size="sm"
            >
              Vložit VIP
            </CustomLink>
          </Stack>
        </Grid>
      </Box>
    </>
  );
};

export default Pricelist;
